import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";

const TermsOfUse = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Terms of Use | EvGateway"
        description="Read EvGateway's Terms of Use governing your access and use of our EV charging management platform and services."
      />
      <Navbar />
      
      <main className="pt-24 pb-16">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="mb-8">
            <h1 className="text-4xl md:text-5xl font-bold text-primary mb-4">EvGateway Terms of Use</h1>
            <p className="text-lg text-muted-foreground">
              <span className="font-semibold">Effective Date:</span> Jan 13th 2026
            </p>
          </div>
          
          <div className="prose prose-lg dark:prose-invert max-w-none space-y-8 text-muted-foreground">
            <p className="text-lg leading-relaxed">
              These Terms of Use ("Terms") are a legally binding agreement between you ("User," "you," or "your") and <strong className="text-foreground">EvGateway</strong>, a company ("EvGateway," "we," "us," or "our"). These Terms govern your access to and use of EvGateway's websites, mobile applications, electric vehicle charging network services, software, content, and any other products or services provided by EvGateway (collectively, the "Services").
            </p>

            <p className="text-lg leading-relaxed">
              By accessing or using the Services, you agree to be bound by these Terms and by our <a href="/privacy-policy" className="text-primary hover:underline font-medium">Privacy Policy</a>, which is incorporated herein by reference. If you do not agree to these Terms, do not use the Services.
            </p>

            <section>
              <h2 className="text-2xl font-semibold text-primary mt-10 mb-4">1. Eligibility</h2>
              <p>
                You must be at least 16 years old (or the minimum age required by local law) to use the Services. By accessing or using the Services, you represent and warrant that you meet this requirement and are capable of forming a binding contract with EvGateway.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-primary mt-10 mb-4">2. Definitions</h2>
              <ul className="list-disc pl-6 space-y-3">
                <li><strong className="text-foreground">Account:</strong> An individual or business profile created by a User to access parts of the Services.</li>
                <li><strong className="text-foreground">Content:</strong> All data, text, graphics, photos, videos, and information available through the Services.</li>
                <li><strong className="text-foreground">Charging Network:</strong> The infrastructure and software that enables the operation and management of EV chargers under the EvGateway platform.</li>
                <li><strong className="text-foreground">Business Customer:</strong> A fleet operator, site host, utility, or enterprise entity using the Services under a separate agreement.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-primary mt-10 mb-4">3. Access and Use of the Services</h2>
              
              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">3.1 License</h3>
              <p>
                Subject to your compliance with these Terms, EvGateway grants you a limited, non-exclusive, non-transferable, non-sublicensable license to access and use the Services solely for your personal or internal business use.
              </p>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">3.2 Account Registration</h3>
              <p>Certain features of the Services may require an Account. You agree to:</p>
              <ul className="list-disc pl-6 space-y-2 mt-2">
                <li>Provide accurate and up-to-date information.</li>
                <li>Maintain the security and confidentiality of your Account credentials.</li>
                <li>Notify EvGateway immediately of any unauthorized use of your account.</li>
              </ul>
              <p className="mt-3">We reserve the right to suspend or terminate Accounts at our discretion.</p>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">3.3 User Conduct</h3>
              <p>You agree not to:</p>
              <ul className="list-disc pl-6 space-y-2 mt-2">
                <li>Use the Services for illegal, harmful, or fraudulent purposes.</li>
                <li>Interfere with the operation, security, or integrity of the Services.</li>
                <li>Reverse-engineer, decompile, or attempt to derive source code from the Services.</li>
                <li>Upload or transmit any viruses, malicious code, or harmful data.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-primary mt-10 mb-4">4. Subscription, Billing, and Payments</h2>
              <p>
                Where applicable, access to certain Services may be subject to fees. If fees apply, you agree to pay all charges at the rates in effect when the charges were incurred.
              </p>
              <p className="mt-4">
                Payments and billing are processed through secure third-party payment processors. EvGateway does not store full payment card numbers; it may store tokens or limited metadata per PCI-DSS compliance requirements.
              </p>
              <p className="mt-4">
                EvGateway may change fees or add new charges, with notice to you as required by law.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-primary mt-10 mb-4">5. Charging Network and Service Permissions</h2>
              <p>To provide Services (including remote management, diagnostics, session tracking, and notifications), you authorize EvGateway to:</p>
              <ul className="list-disc pl-6 space-y-2 mt-2">
                <li>Transmit commands to charging stations.</li>
                <li>Collect and process charging session data.</li>
                <li>Access certain data from connected vehicles, chargers, partners, or integrated platforms (where authorized).</li>
              </ul>
              <p className="mt-4">
                You agree to follow all posted instructions and safety guidelines when using charging stations.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-primary mt-10 mb-4">6. Intellectual Property</h2>
              <p>
                EvGateway and its licensors retain all rights, title, and interest in the Services and all Content, including intellectual property rights. You are granted no rights to use EvGateway trademarks, logos, or proprietary content except as expressly permitted.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-primary mt-10 mb-4">7. Third-Party Services and Integrations</h2>
              <p>
                The Services may integrate with or link to third-party products, services, or platforms (e.g., roaming partners, vehicle telematics). EvGateway does not control such third parties and is not responsible for their terms, conduct, or privacy practices.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-primary mt-10 mb-4">8. Privacy and Data Use</h2>
              <p>
                Your privacy is important to us. Our collection and use of your information are governed by our <a href="/privacy-policy" className="text-primary hover:underline font-medium">Privacy Policy</a>. By using the Services, you consent to the collection and use described in that policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-primary mt-10 mb-4">9. Disclaimers and Warranties</h2>
              <p className="uppercase text-sm">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS, IMPLIED, STATUTORY, OR OTHERWISE. EVGATEWAY DISCLAIMS ALL IMPLIED WARRANTIES, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-primary mt-10 mb-4">10. Limitation of Liability</h2>
              <p className="uppercase text-sm">
                IN NO EVENT SHALL EVGATEWAY, ITS AFFILIATES, OR LICENSORS BE LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL, PUNITIVE, OR CONSEQUENTIAL DAMAGES ARISING OUT OF OR RELATED TO THESE TERMS OR THE SERVICES, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
              </p>
              <p className="uppercase text-sm mt-4">
                OUR TOTAL AGGREGATE LIABILITY FOR ALL CLAIMS ARISING OUT OF OR RELATING TO THESE TERMS OR THE SERVICES SHALL NOT EXCEED THE GREATER OF (A) THE AMOUNTS PAID BY YOU TO EVGATEWAY FOR THE SERVICES IN THE PRIOR 12 MONTHS OR (B) $100.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-primary mt-10 mb-4">11. Indemnification</h2>
              <p>
                You agree to indemnify, defend, and hold harmless EvGateway and its officers, directors, employees, licensors, and agents from any claims, liabilities, damages, losses, or expenses (including reasonable attorneys' fees) arising out of or related to your breach of these Terms or your use of the Services.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-primary mt-10 mb-4">12. Modifications to Terms</h2>
              <p>
                EvGateway may modify these Terms at any time. We will provide notice of material changes as required by law. Your continued use of the Services after changes are posted constitutes your acceptance of the updated Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-primary mt-10 mb-4">13. Governing Law and Dispute Resolution</h2>
              <p>
                These Terms and any dispute arising out of or related to them shall be governed by the laws of the State of California, without regard to conflict of laws principles.
              </p>
              <p className="mt-4">
                Any dispute, claim, or controversy arising out of or relating to these Terms shall be resolved exclusively in the state or federal courts located in Orange County, California, and you submit to the personal jurisdiction of those courts.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-primary mt-10 mb-4">14. Termination</h2>
              <p>
                EvGateway may suspend or terminate your access to the Services at any time, with or without cause, and without notice. Upon termination, all rights granted to you under these Terms will cease.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-primary mt-10 mb-4">15. Entire Agreement</h2>
              <p>
                These Terms and the Privacy Policy constitute the entire agreement between you and EvGateway regarding the Services and supersede all prior or contemporaneous agreements, representations, or understandings.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-primary mt-10 mb-4">16. Contact Information</h2>
              <p>If you have questions about these Terms, please contact:</p>
              <div className="mt-4 p-6 bg-muted/50 rounded-2xl border border-border/50">
                <p className="font-semibold text-foreground text-lg">EvGateway</p>
                <p className="mt-2">
                  <span className="font-medium text-foreground">Email:</span>{" "}
                  <a href="mailto:support@evgateway.com" className="text-primary hover:underline">support@evgateway.com</a>
                </p>
                <p className="mt-1">
                  <span className="font-medium text-foreground">Address:</span> 19681 Da Vinci, Foothill Ranch, CA 92610
                </p>
              </div>
            </section>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default TermsOfUse;
